import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Minus, Loader2, Layers } from "lucide-react";
import { api, apiError, rupiah } from "@/lib/api";
import { PageHeader, StatCard, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function SaldoPage() {
  const [data, setData] = useState({ items: [], total_saldo: 0, jumlah_anggota: 0 });
  const [loading, setLoading] = useState(true);
  const [adjust, setAdjust] = useState(null);
  const [nominal, setNominal] = useState("");
  const [ket, setKet] = useState("");
  const [massOpen, setMassOpen] = useState(false);
  const [massNominal, setMassNominal] = useState("");
  const [massKet, setMassKet] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/saldo");
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitAdjust = async (sign) => {
    const val = Number(nominal);
    if (!val) return toast.error("Nominal tidak valid");
    setBusy(true);
    try {
      await api.post(`/saldo/${encodeURIComponent(adjust.nama_panggilan)}/adjust`, {
        nominal: sign * Math.abs(val),
        keterangan: ket,
      });
      toast.success(`Saldo ${adjust.nama_panggilan} diperbarui`);
      setAdjust(null);
      setNominal("");
      setKet("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitMass = async () => {
    const val = Number(massNominal);
    if (!val) return toast.error("Nominal tidak valid");
    setBusy(true);
    try {
      const res = await api.post("/saldo/mass", { nominal: val, keterangan: massKet });
      toast.success(
        `Saldo massal terkirim ke ${res.data.terproses} anggota aktif` +
          (res.data.total_bonus_downline ? ` · bonus downline ${rupiah(res.data.total_bonus_downline)}` : "") +
          (res.data.dilewati ? ` · ${res.data.dilewati} anggota nonaktif dilewati` : "")
      );
      setMassOpen(false);
      setMassNominal("");
      setMassKet("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="saldo-page">
      <PageHeader
        eyebrow="Saldo Anggota"
        title="Kelola Saldo & Mutasi"
        description="Setiap penyesuaian saldo otomatis tercatat pada rincian mutasi dashboard anggota terkait."
      >
        <Button
          data-testid="mass-balance-btn"
          onClick={() => setMassOpen(true)}
          className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
        >
          <Layers size={16} className="mr-1.5" /> Input Saldo Massal
        </Button>
      </PageHeader>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard testId="stat-total-saldo" label="Total Saldo Terkelola" value={rupiah(data.total_saldo)} accent="profit" />
        <StatCard testId="stat-jumlah-anggota" label="Anggota Aktif / Total" value={`${data.jumlah_aktif ?? 0} / ${data.jumlah_anggota}`} />
        <StatCard
          testId="stat-rata-saldo"
          label="Rata-rata Saldo"
          value={rupiah(data.jumlah_anggota ? data.total_saldo / data.jumlah_anggota : 0)}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-[#1e2536] bg-[#10141d]">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#2b3550]" />
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={Wallet} title="Belum ada saldo anggota" hint="Tambahkan anggota terlebih dahulu." />
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="saldo-table" className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-[#1e2536] bg-[#0d1119] text-left text-xs uppercase tracking-wider text-[#4d586f]">
                  <th className="px-4 py-3 font-medium">Panggilan</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Nama Lengkap</th>
                  <th className="px-4 py-3 font-medium">Sekuritas</th>
                  <th className="px-4 py-3 font-medium">Saldo Terkini</th>
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} data-testid={`saldo-row-${r.nama_panggilan}`} className="border-b border-[#1a2030] last:border-0 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 font-semibold text-[#00d09c]">{r.nama_panggilan}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs uppercase tracking-wider ${
                          r.aktif !== false
                            ? "bg-[#00d09c]/10 text-[#00d09c]"
                            : "bg-[#ff3b30]/10 text-[#ff3b30]"
                        }`}
                      >
                        {r.aktif !== false ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white">{r.nama_lengkap || "-"}</td>
                    <td className="px-4 py-3 text-[#8f9bb3]">{r.sekuritas || "-"}</td>
                    <td className="font-mono-num px-4 py-3 font-semibold text-white">{rupiah(r.saldo)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        data-testid={`saldo-edit-btn-${r.nama_panggilan}`}
                        variant="outline"
                        onClick={() => setAdjust(r)}
                        className="h-8 border-[#1e2536] bg-transparent text-xs text-[#8f9bb3] hover:text-[#00d09c]"
                      >
                        Edit Saldo
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!adjust} onOpenChange={(v) => !v && setAdjust(null)}>
        <DialogContent data-testid="saldo-adjust-dialog" className="border-[#1e2536] bg-[#10141d]">
          <DialogHeader>
            <DialogTitle className="text-white">Penyesuaian Saldo — {adjust?.nama_panggilan}</DialogTitle>
          </DialogHeader>
          <p className="font-mono-num text-sm text-[#8f9bb3]">
            Saldo saat ini: <span className="text-white">{rupiah(adjust?.saldo)}</span>
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Nominal (Rp)</Label>
              <Input
                data-testid="saldo-nominal-input"
                type="number"
                value={nominal}
                onChange={(e) => setNominal(e.target.value)}
                className="font-mono-num mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Keterangan</Label>
              <Input
                data-testid="saldo-keterangan-input"
                value={ket}
                onChange={(e) => setKet(e.target.value)}
                placeholder="mis. Top up, Bagi hasil IPO"
                className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="saldo-kurang-btn"
              disabled={busy}
              onClick={() => submitAdjust(-1)}
              variant="outline"
              className="border-[#ff3b30]/40 bg-transparent text-[#ff3b30] hover:bg-[#ff3b30]/10"
            >
              <Minus size={15} className="mr-1.5" /> Kurangi
            </Button>
            <Button
              data-testid="saldo-tambah-btn"
              disabled={busy}
              onClick={() => submitAdjust(1)}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              <Plus size={15} className="mr-1.5" /> Tambah
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={massOpen} onOpenChange={setMassOpen}>
        <DialogContent data-testid="mass-balance-modal" className="border-[#1e2536] bg-[#10141d]">
          <DialogHeader>
            <DialogTitle className="text-white">Input Saldo Massal</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#8f9bb3]">
            Nominal akan ditambahkan ke saldo{" "}
            <span className="text-white">{data.jumlah_aktif ?? 0} anggota berstatus AKTIF</span> sekaligus.
            Anggota nonaktif otomatis dilewati. Anggota yang punya downline mendapat bonus tambahan
            10% dari nominal per downline. Gunakan nilai negatif untuk pengurangan massal.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Nominal (Rp)</Label>
              <Input
                data-testid="mass-nominal-input"
                type="number"
                value={massNominal}
                onChange={(e) => setMassNominal(e.target.value)}
                className="font-mono-num mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Keterangan</Label>
              <Input
                data-testid="mass-keterangan-input"
                value={massKet}
                onChange={(e) => setMassKet(e.target.value)}
                placeholder="mis. Bagi hasil IPO Januari"
                className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMassOpen(false)} className="border-[#1e2536] bg-transparent text-[#8f9bb3]">
              Batal
            </Button>
            <Button
              data-testid="mass-send-btn"
              disabled={busy}
              onClick={submitMass}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Kirim"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
